import { defineStore } from "pinia";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useAuthStore } from "./auth.store";
import { useConfigStore } from "./config.store";
import { isSG } from "@/config/appVersion";

// Prefiks nazw grup zarezerwowany dla kont serwisowych (_SUPERUSER, _RTC, _VEH-CREATOR, ...).
// Takie konta są ukryte na listach i chronione przed edycją dla każdego bez ROLE_6.
// Musi być spójne z `backend/src/Entity/Current/Access/GroupRoles.php#SERVICE_NAME_PREFIX`.
const SERVICE_GROUP_PREFIX = "_";

// Prefiks LOGINU zarezerwowany dla kont obsługi technicznej. Odrębne kryterium od prefiksu
// grupy powyżej: konto techniczne bywa przypisane do zwykłej grupy (np. ADMINISTRATOR), a i tak
// nie może być widoczne ani edytowalne dla administratora.
// Musi być spójne z `backend/src/Entity/Current/Access/User.php#SERVICE_USERNAME_PREFIX`.
const SERVICE_USERNAME_PREFIX = "_";

// Ranga w hierarchii tierów administratorskich — wyższa liczba = wyżej. 0 oznacza grupę spoza
// tej hierarchii (np. operatora). Musi być spójne z `Permissions::ADMIN_RANKS` na BE.
const adminRank = (name) => {
  if (name === "administrator") return 2;
  if (name === "administrator bez list" || name === "administrator abz") return 1;
  return 0;
};

// Nazwy grup ról obsługi technicznej — dwa najwyższe szczeble zakładki „Zarządzanie rolami".
// Muszą być spójne z `Permissions::GROUP_DEVELOPER` / `GROUP_SUPERUSER` na BE.
const GROUP_DEVELOPER = "_developer";
const GROUP_SUPERUSER = "_superuser";

// Zakresy uprawnień, którymi wolno zarządzać w zakładce „Zarządzanie rolami" — czyli te,
// które użytkownik może dopisać do grupy ról ORAZ z niej odjąć (własnej lub cudzej).
//
// Zakresy są STAŁYMI listami, a nie „tym, co user aktualnie posiada" — dzięki temu admin może
// odjąć sobie uprawnienie ze swojej grupy i później dodać je z powrotem.
//
// Muszą być spójne z `Permissions::MANAGEABLE_PERMISSIONS_*` na BE.

// Deweloper — wszystkie uprawnienia, łącznie z tymi do elementów budowanych (ROLE_21..24),
// oraz ROLE_27 (alerty monitoringu stacji).
const MANAGEABLE_PERMISSIONS_DEVELOPER = [
  ...Array.from({ length: 24 }, (_, i) => `ROLE_${i + 1}`),
  "ROLE_27",
];

// Superuser — ROLE_1..20 oraz ROLE_27, bez uprawnień do elementów budowanych i nieprzetestowanych.
const MANAGEABLE_PERMISSIONS_SUPERUSER = [
  ...Array.from({ length: 20 }, (_, i) => `ROLE_${i + 1}`),
  "ROLE_27",
];

// Administrator — stała lista ROLE_1..5, 7, 8, 9, 11, 12, 15, 27. Poza nią m.in. ROLE_6 (wgląd
// w grupy serwisowe), ROLE_10 (stacje), ROLE_13/14 (kalibracje), ROLE_16 (router),
// ROLE_17/18/19 (listy i kontakty), ROLE_20 (anonimizacja), ROLE_21..24 (diagnostyka, kamery).
const MANAGEABLE_PERMISSIONS_ADMIN = [
  "ROLE_1",
  "ROLE_2",
  "ROLE_3",
  "ROLE_4",
  "ROLE_5",
  "ROLE_7",
  "ROLE_8",
  "ROLE_9",
  "ROLE_11",
  "ROLE_12",
  "ROLE_15",
  "ROLE_27",
];

// Store do obsługi danych aktualnie zalogowanego użytkownika oraz preferencji
export const useUserStore = defineStore({
  id: "user",
  state: () => ({
    admin: null,          // Czy aktualny użytkownik ma uprawnienia admina?
    currentUser: null,    // Dane zalogowanego użytkownika
    preferences: null,    // Preferencje użytkownika
    vehicleListDisplayProperties: [], // Dostępne właściwości listy pojazdów do wyboru
    // Dane zgłaszającego do formularza „Zgłoś problem" (modal spod ikony „!”): { name, email }.
    // Wyliczane z profilu zaraz po jego dociągnięciu, więc modal otwiera się z wypełnionymi
    // polami, bez żądania do API. `null` = brak profilu albo brak uprawnienia ROLE_11.
    ticketReporter: null,
    _vehicleListPropsEndpointAvailable: null, // null=nieznane, false=brak endpointu (404), true=dostępny
    _selfPromise: null,
  }),
  actions: {
    // Resetowanie stanu użytkownika (wylogowanie, czyszczenie preferencji)
      reset() {
          this.admin = null;
          this.currentUser = null;
          this.preferences = null;
          this.vehicleListDisplayProperties = [];
          this.ticketReporter = null;
          this._loadedForToken = null;
          this._vehicleListPropsEndpointAvailable = null;
          this._selfPromise = null;
      },
    // Aktualizacja danych użytkownika (np. imię, email)
    async update(payload) {
      try {
        const updateRes = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/users/self/update`,
          payload
        );
        return { result: updateRes, error: null };
      } catch (error) {
        // console.log(error);
        return { result: null, error: error };
      }
    },
    // Aktualizacja ustawień/preferencji użytkownika
    async updateSettings(payload) {
      try {
        const updateRes = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/users/self/settings`,
          payload
        );
        this.preferences = updateRes;
        return { result: updateRes, error: null };
      } catch (error) {
        // console.log(error);
        return { result: null, error: error };
      }
    },
    // Zmiana hasła przez użytkownika
    async updatePassword(payload) {
      try {
        const updateRes = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/users/self/update-password`,
          payload
        );
        return { result: updateRes, error: null };
      } catch (error) {
        // console.log(error);
        return { result: null, error: error };
      }
    },
    // Pobierz własne dane i preferencje użytkownika (jeśli jeszcze nie są załadowane)
      /**
       * force=true -> zawsze pobierz z API
       * force=false -> pobierz tylko jeśli nie ma danych albo token się zmienił
       */
      async self(force = false) {
          const auth = useAuthStore();
          if (!auth.token) {
              this.reset();
              return null;
          }

          if (!force && this._selfPromise) {
              return this._selfPromise;
          }

          const tokenChanged = this._loadedForToken !== auth.token;

          if (!force && this.currentUser && !tokenChanged) {
              return this.currentUser;
          }

          // jeśli token się zmienił, wyczyść stan zanim pobierzesz
          if (tokenChanged) {
              this.reset();
          }

          this._selfPromise = (async () => {
              const userRes = await fetchWrapper.get(
                  `${process.env.VUE_APP_API_URL}/users/self`
              );

              // UWAGA: nie mutuj obiektu z backendu w miejscu, zrób kopię
              const u = { ...userRes };

              if (u.phoneNumber) {
                  u.phoneNumberDir = u.phoneNumber.substring(0, 3);
                  u.phoneNumber = u.phoneNumber.substring(3);
              } else {
                  u.phoneNumberDir = "+48";
                  u.phoneNumber = "";
              }

              const prefRes = await fetchWrapper.get(
                  `${process.env.VUE_APP_API_URL}/users/self/settings`
              );

              let metricsRes =
                  prefRes?.vehicleListDisplayProperties ??
                  prefRes?.vehicleListProperties;

              if (!Array.isArray(metricsRes) && this._vehicleListPropsEndpointAvailable !== false) {
                  try {
                      metricsRes = await fetchWrapper.get(
                          `${process.env.VUE_APP_API_URL}/users/self/settings/vehicle-list-properties`
                      );
                      this._vehicleListPropsEndpointAvailable = true;
                  } catch (e) {
                      metricsRes = [];
                      if (e === 404 || e === "404") {
                          this._vehicleListPropsEndpointAvailable = false;
                      }
                  }
              }

              if (!Array.isArray(metricsRes)) {
                  metricsRes = [];
              }

              this.currentUser = u;
              // Flagi funkcji przyjeżdżają razem z profilem — zasilamy nimi config store
              // od razu, żeby menu i guardy tras nie musiały czekać na osobne żądanie
              // (`/app/feature-flags` zostaje wyłącznie fallbackiem dla starszego API).
              useConfigStore().applyFlags(u.featureFlags);
              this.preferences = prefRes;
              this.vehicleListDisplayProperties = Array.isArray(metricsRes) ? metricsRes : [];
              this.admin = null; // przelicz role od nowa
              this._loadedForToken = auth.token;
              // Dane do formularza zgłoszenia liczymy od razu — modal ma je mieć gotowe
              // w chwili kliknięcia ikony „!”, bez dobijania się po nie do API.
              this.refreshTicketReporter();

              return this.currentUser;
          })();

          try {
              return await this._selfPromise;
          } finally {
              this._selfPromise = null;
          }
      },
    // Przelicza dane zgłaszającego (imię i nazwisko + e-mail) z aktualnego profilu.
    // Dane trzyma wyłącznie użytkownik, który w ogóle może otworzyć modal zgłoszenia
    // (ROLE_11 / item „Tickets") — bez uprawnienia cache zostaje pusty.
    refreshTicketReporter() {
      if (!this.currentUser || !this.getItemAccess("Tickets")) {
        this.ticketReporter = null;
        return null;
      }
      this.ticketReporter = {
        name: [this.currentUser.firstName, this.currentUser.lastName]
          .filter(Boolean)
          .join(" ")
          .trim(),
        email: this.currentUser.email ?? "",
      };
      return this.ticketReporter;
    },
    // Dociągnięcie danych zgłaszającego w tle — wołane raz, po starcie aplikacji.
    // `self()` jest cache'owane i deduplikowane, więc jeśli profil już leci (guard routera)
    // albo jest w store, nie powstaje dodatkowe żądanie. Błędy celowo połykamy: to prefetch
    // wygody, nie może blokować ani psuć startu aplikacji.
    async prefetchTicketReporter() {
      if (!useAuthStore().token) {
        return null;
      }
      try {
        await this.self();
      } catch (e) {
        return null;
      }
      return this.refreshTicketReporter();
    },
    // Sprawdź i ustaw, czy użytkownik ma uprawnienia administratora (ROLE_7)
    async roles() {
      if (this.currentUser === null) {
        await this.self();
      }

      if (!this.currentUser || !this.currentUser.roles) {
        this.admin = false;
        return;
      }

      if (this.admin === null) {
        const hasRole7 = this.currentUser.roles[0]?.permissions?.some(
          (perm) => perm.name === "ROLE_7"
        );
        this.admin = !!hasRole7;
      }
    },
    // Sprawdzenie czy użytkownik ma dostęp do określonych zasobów/funkcjonalności (np. WantedList, Stat itd.)
    hasAccess(items) {
      this.roles(); // Upewnij się, że role są wczytane

      const itemArr = items.split(",");

      for (const itemCheck in itemArr) {
        if (this.getItemAccess(itemArr[itemCheck])) {
          return true; // Ma dostęp do przynajmniej jednej funkcji
        }
      }
      return false;
    },
    // Mapowanie nazw uprawnień do konkretnych ról systemowych (np. ROLE_17 = BlackList)
    getItemAccess(item) {
      if (item === "CurrentMeasurements") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za bieżące pomiary.
        return this.checkRole("ROLE_1");
      }
      if (item === "ArchiveMeasurements") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za archiwalne pomiary.
        return this.checkRole("ROLE_2");
      }
      if (item === "Vms") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za sterowanie znakami zmiennej treści
        return this.checkRole("ROLE_3");
      }
      if (item === "Stat") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za statystyki.
        return this.checkRole("ROLE_4");
      }
      if (item === "Roles") {
        // Umożliwia dostęp do wszystkich ról, które istnieją w systemie, za wyjątkiem tych specjalnych, które są przeznaczone dla specjalnych użytkowników.
        return this.checkRole("ROLE_5");
      }
      if (item === "Anonymization") {
        // Umożliwia wyłączenie nietrwałej anonimizacji dla użytkowników posiadających to uprawnienie.
        return this.checkRole("ROLE_20");
      }
      if (item === "WantedList") {
        // Globalne uprawnienie LUB członkostwo w grupie kontaktów (dziedziczone przez
        // dopasowany Contact -> User po e-mailu / numerze telefonu).
        return this.checkRole("ROLE_17") || this.checkRole("ROLE_18") || this.checkRole("ROLE_19") || this.isWantedListGroupMember();
      }

      /*
        Role - panel admina:
        7, 8, 12, 17, 19, 20, 22, 27, 33, 34
      */

      if (item === 'Admin') {
        return this.checkRole("ROLE_5") || this.checkRole("ROLE_6");
      }
      if (item === 'UserManagement') {
        // Ścisłe "zarządzanie kontami użytkowników" (CRUD userów). ROLE_5 = listing
        // zwykłych ról, ROLE_6 = listing wszystkich ról (włącznie ze specjalnymi).
        return this.checkRole("ROLE_7");
        // let role7 = this.checkRole("ROLE_7");
        // console.log("UserManagement" + role7);
        // return role7;
      }
      if (item === 'UserActivity') {
        // Historia aktywności użytkowników — ROLE_8
        return this.checkRole("ROLE_8");
        // let role8 = this.checkRole("ROLE_8");
        // console.log("UserActivity" + role8);
        // return role8;
      }
      if (item === 'RoleManagement') {
        // Pełne zarządzanie grupami ról (CRUD ról i ich uprawnień). Wymaga ROLE_6
        // — czyli adminów z wglądem w *wszystkie* role, łącznie ze specjalnymi.
        // ROLE_5 daje tylko prawo przeglądania regularnych ról na potrzeby
        // przypisania userowi (dropdown w AddUser/EditUser) — to za mało, żeby
        // wchodzić do widoku zarządzania samymi rolami.
        return this.checkRole("ROLE_9");
        // let role9 = this.checkRole("ROLE_9");
        // console.log("RoleManagement" + role9);
        // return role9;
      }
      if (item === 'LocationManagement') {
        // Zarządzanie stacjami preselekcji (CRUD lokalizacji + pasów ruchu). Dedykowane
        // uprawnienie zapisu ROLE_10 — niezależne od odczytowego ROLE_29. Musi być spójne
        // z PermissionCode::LOCATION_MANAGEMENT na BE. Dodatkowo w routingu/menu wymagana
        // jest włączona flaga funkcji (configStore.featureFlags.locationManagement).
        return this.checkRole("ROLE_10");
        // let role10 = this.checkRole("ROLE_10");
        // console.log("LocationManagement" + role10);
        // return role10;
      }

      if (item === 'MonitoringAlerts') {
        // Alerty infrastrukturalne stacji preselekcji (problemy z Zabbixa) — ROLE_27, spójne
        // z PermissionCode::MONITORING_ALERTS na BE. Dodatkowo w routingu/menu wymagana jest
        // włączona flaga funkcji (configStore.featureFlags.monitoringAlerts) i wariant inny niż SG.
        return this.checkRole("ROLE_27");
      }

      if (item === 'Tickets') {
        // ROLE_11 (PermissionCode::TICKETS) — WYŁĄCZNIE wysyłka zgłoszenia formularzem
        // (ikona „!” w nagłówku). NIE daje wglądu w zakładkę „Zgłoszenia”.
        return this.checkRole("ROLE_11");
      }

      if (item === 'ReportsManagement') {
        // ROLE_12 (PermissionCode::REPORTS_MANAGEMENT) — dostęp do zakładki „Zgłoszenia”
        // (lista, podgląd, edycja, załączniki). Spójne z TicketController na BE.
        return this.checkRole("ROLE_12");
      }

      if (item === 'CalibrationManagement') {
        // Diagnostyka kalibracji wag (kalibracja + dewiacja kalibracji). Musi być spójne
        // z PermissionCode::CALIBRATION_MANAGEMENT (ROLE_13) egzekwowanym w CalibrationController.
        return this.checkRole("ROLE_13");
        // let role13 = this.checkRole("ROLE_13");
        // console.log("CalibrationManagement" + role13);
        // return role13;
      }

      if (item === 'CalibrationDeviation') {
        return this.checkRole("ROLE_14");
        // let role14 = this.checkRole("ROLE_14");
        // console.log("CalibrationDeviation" + role14);
        // return role14;
      }

      if (item === 'LocationStatusHistoryRouter') {
        // ROLE_16 (PermissionCode::LOCATION_STATUS_HISTORY_ROUTER) — samo odblokowanie
        // przycisku „Otwórz router" w zakładce „Historia statusów lokalizacji".
        // Wejście do zakładki daje ROLE_15, to uprawnienie NIE zastępuje go.
        return this.checkRole("ROLE_16");
      }

      if (item === 'LocationStatusHistory') {
        return this.checkRole("ROLE_15");
        // let role15 = this.checkRole("ROLE_15");
        // console.log("LocationStatusHistory" + role15);
        // return role15;
      }

      if (item === 'BlackList') {
        // Globalne uprawnienie (ROLE_17) LUB member-by-group (read + dodawanie własnych tablic).
        return this.checkRole("ROLE_17") || this.isWantedListGroupMember();
        // let role17 = this.checkRole("ROLE_17") || this.isWantedListGroupMember();
        // console.log("BlackList" + role17);
        // return role17;
      }

      if (item === 'WhiteList') {
        // Tylko globalne uprawnienie (ROLE_18). Member-by-group NIE dostaje wglądu do białej listy
        // — przynależność do grupy kontaktów daje wyłącznie dostęp do czarnej listy.
        return this.checkRole("ROLE_18");
        // let role18 = this.checkRole("ROLE_18");
        // console.log("WhiteList" + role18);
        // return role18;
      }

      if (item === 'Contacts') {
        // Tylko globalne uprawnienie (ROLE_19). Member-by-group NIE widzi listy kontaktów —
        // gdyby widział, mógłby przeglądać dane kontaktowe innych członków swojej grupy.
        return this.checkRole("ROLE_19");
        // let role19 = this.checkRole("ROLE_19");
        // console.log("Contacts" + role19);
        // return role19;
      }

      return false;
    },
    // Sprawdź, czy aktualny użytkownik ma określoną rolę)
    checkRole(id) {
      return this.currentUser?.roles?.[0]?.permissions?.some(
        (role) => role.name === id
      ) || false;
    },
    // Czy zalogowany użytkownik jest członkiem grupy kontaktów list poszukiwanych
    // (przez dopasowany Contact::linkedUser). Flaga liczona po stronie BE,
    // przesyłana w odpowiedzi /users/self.
    isWantedListGroupMember() {
      return this.currentUser?.wantedListGroupMember === true;
    },
    // Czy zalogowany użytkownik ma globalne uprawnienie modyfikacji list (white/black).
    // Member-by-group nie spełnia tego warunku — może edytować tylko własne tablice.
    canModifyWantedListsGlobally() {
      return this.checkRole("ROLE_17") || this.checkRole("ROLE_18");
    },
    // Czy zalogowany użytkownik może edytować/usunąć konkretną tablicę poszukiwaną.
    //
    // Tablica nie ma już "właściciela" — jedyną podstawą edycji w trybie member-by-group
    // jest członkostwo w grupie kontaktów spiętej z tą tablicą. Globalne uprawnienie
    // (ROLE_17 / ROLE_18) przepuszcza zawsze.
    //
    // Sygnatura: { contactGroupIds: number[] } (z mapVehicleFromApi).
    canEditWantedPlate(plate) {
      if (this.canModifyWantedListsGlobally()) {
        return true;
      }
      const plateGroups = Array.isArray(plate?.contactGroupIds) ? plate.contactGroupIds : [];
      const myGroups = Array.isArray(this.currentUser?.wantedListContactGroupIds)
        ? this.currentUser.wantedListContactGroupIds
        : [];
      if (plateGroups.length === 0 || myGroups.length === 0) {
        return false;
      }
      return plateGroups.some(id => myGroups.includes(id));
    },
    // Czy zalogowany użytkownik może zarządzać kontaktami (CRUD). Member nie może.
    canManageContactsGlobally() {
      return this.checkRole("ROLE_19");
    },
    // Zakres uprawnień, którymi zalogowany user może zarządzać w zakładce „Zarządzanie
    // rolami" — czyli te, które wolno mu dopisać do grupy ról ORAZ z niej odjąć.
    //
    // Szczebel wyznacza NAZWA grupy ról usera (user ma maksymalnie jedną grupę):
    //   1. `_DEVELOPER`       → ROLE_1..24
    //   2. `_SUPERUSER`       → ROLE_1..20
    //   3. ROLE_6 (ALL_ROLES) → zakres dewelopera; furtka zgodności dla grup serwisowych
    //                           wdrożeń, które nie nazywają się `_DEVELOPER`.
    //   4. administrator      → stała lista admina; rozpoznawany po randze w hierarchii
    //                           (`adminRank`) albo po ROLE_5.
    //   5. pozostali          → pusty zakres; operator i tak nie ma dostępu do zakładki.
    //
    // Musi być spójne z `backend/src/Security/Permissions.php#getManageablePermissions()`.
    manageablePermissions() {
      const myRoleName = String(this.currentUser?.roles?.[0]?.name ?? "")
        .trim()
        .toLowerCase();

      if (myRoleName === GROUP_DEVELOPER) {
        return MANAGEABLE_PERMISSIONS_DEVELOPER;
      }
      if (myRoleName === GROUP_SUPERUSER) {
        return MANAGEABLE_PERMISSIONS_SUPERUSER;
      }
      if (this.checkRole("ROLE_6")) {
        return MANAGEABLE_PERMISSIONS_DEVELOPER;
      }
      if (adminRank(myRoleName) > 0 || this.checkRole("ROLE_5")) {
        return MANAGEABLE_PERMISSIONS_ADMIN;
      }
      return [];
    },
    // Czy user może nadać LUB odjąć dane uprawnienie w grupie ról. Uprawnienia spoza jego
    // zakresu są dla niego niezmienne — nie doda ich ani nie usunie (BE egzekwuje to samo
    // w POST /roles i PUT /roles/{id}).
    //
    // Ta reguła NIE rządzi dropdownem ról przy AddUser/EditUser — tam decyduje hierarchia
    // po NAZWIE grupy (`canAssignRoleGroup`).
    canGrantPermission(permissionName) {
      return this.manageablePermissions().includes(permissionName);
    },
    // Docelowa ścieżka po zalogowaniu (i fallback guardu, gdy user wejdzie na trasę bez
    // uprawnień) — zależy od wariantu wdrożenia i uprawnień usera.
    //
    // Wariant SG ma ukryty widok "/" (CurrentMeasurements), więc startuje od "/archival".
    // W non-SG startujemy od "/", ale TYLKO gdy user ma ROLE_1 — inaczej lądowałby na
    // widoku bieżących pomiarów bez uprawnień (albo wpadał w pętlę redirectów, odkąd
    // trasa "/" jest chroniona przez meta.access).
    //
    // Dalej lecimy tym samym łańcuchem: widoki pomiarowe → zarządzanie → listy →
    // diagnostyka, a na końcu Preferencje (dostępne dla KAŻDEGO zalogowanego usera,
    // dlatego ta trasa nie ma `meta.access` — inaczej fallback nie miałby wyjścia).
    //
    // Każda pozycja MUSI odpowiadać nazwie itemu obsługiwanej w `getItemAccess()` —
    // literówka = `false` = przeskok pozycji (dawniej "LocationStatuses").
    landingPath() {
      if (!isSG && this.getItemAccess("CurrentMeasurements")) return "/";
      if (this.getItemAccess("ArchiveMeasurements")) return "/archival";
      if (this.getItemAccess("UserManagement")) return "/user-management";
      if (this.getItemAccess("UserActivity"))   return "/user-activity";
      if (this.getItemAccess("RoleManagement")) return "/role-group-management";
      // Tickets / VMS / Kalibracja są w wariancie SG ukryte w menu (TopMenu:
      // `shouldHideLocation`), więc nie wolno na nie lądować w SG.
      if (!isSG && this.getItemAccess("ReportsManagement")) return "/tickets";
      if (this.getItemAccess("BlackList"))      return "/black-list";
      if (this.getItemAccess("WhiteList"))      return "/white-list";
      if (this.getItemAccess("Contacts"))       return "/contact";
      if (!isSG && this.getItemAccess("Vms"))   return "/vms";
      if (this.getItemAccess("Stat"))           return "/stat";
      if (this.getItemAccess("LocationStatusHistory")) return "/location-statuses";
      if (!isSG && this.getItemAccess("CalibrationManagement")) return "/calibration";
      if (!isSG && this.getItemAccess("CalibrationDeviation")) return "/calibration-deviation";
      // Ostatni fallback — każdy zalogowany user ma dostęp do własnych preferencji.
      return "/preference";
    },
    // Czy admin może przypisać innemu użytkownikowi tę grupę ról.
    //
    // Hierarchia jest po NAZWIE grupy, nie po podzbiorach uprawnień — admin „abz"
    // (4 uprawnienia) musi móc nadać „operatora 1" (kilkanaście uprawnień
    // operatorskich, których sam nie posiada). Reguły (sprawdzane w kolejności):
    //
    //   1. ROLE_6 (full role-manager) — carte blanche, każda grupa.
    //   2. Własna grupa (porównanie po nazwie, case-insensitive) — admin nadaje swoją.
    //   3. Hierarchia tierów administratorskich (patrz `adminRank` niżej):
    //      „administrator" stoi NAD „administrator bez list" / „administrator abz".
    //      Wyższy tier może nadawać role ściśle niższych tierów. Dwa niższe tiery
    //      są równorzędne — NIE widzą siebie nawzajem ani „administrator".
    //   4. Nazwa docelowej grupy zawiera „operator" (case-insensitive) — operatorzy
    //      są zawsze poniżej dowolnego admina w hierarchii.
    //   5. Inaczej — odrzuć.
    //
    // Skutki:
    //   ✓ „administrator" → „administrator bez list", „administrator abz", operatorzy
    //   ✗ „administrator bez list" / „administrator abz" → „administrator" (tier wyżej)
    //   ✗ „administrator bez list" ↔ „administrator abz" (ten sam tier)
    //
    // Musi być spójne z `backend/src/Security/Permissions.php#canAssignRoleGroup()`.
    canAssignRoleGroup(role) {
      if (this.checkRole("ROLE_6")) {
        return true;
      }
      const targetName = String(role?.name ?? "").trim().toLowerCase();
      if (!targetName) {
        return false;
      }
      const myRoleName = String(this.currentUser?.roles?.[0]?.name ?? "").trim().toLowerCase();
      if (myRoleName && targetName === myRoleName) {
        return true;
      }
      return this.outranksRoleGroupName(targetName);
    },
    // Czy zalogowany stoi ŚCIŚLE wyżej od wskazanej grupy — bez reguły „własna grupa".
    // Wspólny rdzeń dla `canAssignRoleGroup` (gdzie własna grupa jest dozwolona, bo admin ma
    // móc założyć kolejnego admina) i dla kontroli operacji na koncie (gdzie własna grupa NIE
    // wystarcza, bo admin nie edytuje ani nie usuwa równego sobie).
    //
    // Musi być spójne z `backend/src/Security/Permissions.php#outranksGroupName()`.
    outranksRoleGroupName(targetName) {
      if (!targetName) {
        return false;
      }
      const myRoleName = String(this.currentUser?.roles?.[0]?.name ?? "").trim().toLowerCase();
      const myRank = adminRank(myRoleName);
      const targetRank = adminRank(targetName);
      if (myRank > 0 && targetRank > 0 && myRank > targetRank) {
        return true;
      }
      return targetName.includes("operator");
    },
    // Czy zalogowany stoi ściśle wyżej od wskazanego KONTA. Konta serwisowe (grupy `_*`) oraz
    // konta bez grupy ról są poza zasięgiem każdego bez ROLE_6.
    //
    // Musi być spójne z `backend/src/Security/Permissions.php#outranksUser()`.
    outranksUser(user) {
      if (this.checkRole("ROLE_6")) {
        return true;
      }
      // Konto obsługi technicznej rozpoznane po LOGINIE (`_cat`, `_kzemanek`) — poza zasięgiem
      // każdego bez ROLE_6, niezależnie od tego, w jakiej grupie ról siedzi. Backend takich kont
      // administratorowi w ogóle nie wysyła (`Permissions::hiddenServiceUserIds()`), ale gdyby
      // trafiło tu inną drogą, przyciski mają zostać wygaszone.
      if (String(user?.username ?? "").trim().startsWith(SERVICE_USERNAME_PREFIX)) {
        return false;
      }
      const groups = Array.isArray(user?.roles) ? user.roles : [];
      if (groups.length === 0) {
        return false;
      }
      return groups.every((group) => {
        const name = String(group?.name ?? "").trim().toLowerCase();
        if (name.startsWith(SERVICE_GROUP_PREFIX)) {
          return false;
        }
        return this.outranksRoleGroupName(name);
      });
    },
    // Czy zalogowany może PODEJRZEĆ i EDYTOWAĆ wskazane konto (przycisk „Edytuj" na liście).
    // Własne konto zawsze; konto równorzędnego administratora nie.
    //
    // Backend egzekwuje to samo w GET/PUT /users/{id} — front tylko wygasza przyciski,
    // musi więc być spójny z `Permissions::canEditUser()`.
    canEditUser(user) {
      if (user?.id != null && user.id === this.currentUser?.id) {
        return true;
      }
      return this.outranksUser(user);
    },
    // Czy zalogowany może USUNĄĆ konto lub wymusić jego wylogowanie. W odróżnieniu od
    // `canEditUser` własne konto NIE wystarcza — administrator nie odcina sam sobie dostępu.
    //
    // Musi być spójne z `backend/src/Security/Permissions.php#canAdministerUser()`.
    canAdministerUser(user) {
      if (user?.id != null && user.id === this.currentUser?.id) {
        return this.checkRole("ROLE_6");
      }
      return this.outranksUser(user);
    },
  },
});
